import { useState } from 'react';
import { useLocation } from 'wouter';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

const mockSettings = {
  notifications: {
    allowAll: true,
    orderUpdates: true,
    socialActivity: true,
    promotional: false,
  },
  privacy: {
    profileVisibility: 'public',
    showActivity: true,
  },
};

export default function SettingsPreferences() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [settings, setSettings] = useState(mockSettings);

  const handleSave = () => {
    // In a real app, this would save to the backend
    toast({
      title: 'Settings updated',
      description: 'Your preferences have been saved.',
    });
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/profile')}
          data-testid="button-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-lg font-semibold">Notifications & Privacy</h1>
        <div className="w-10" /> {/* Spacer for alignment */}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-6">
        <div className="p-4 space-y-6">
          {/* Notifications */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg">Notifications</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Allow all Notifications</div>
                    <div className="text-sm text-muted-foreground">
                      Receive all types of notifications
                    </div>
                  </div>
                  <Switch
                    checked={settings.notifications.allowAll}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, allowAll: checked },
                      })
                    }
                    data-testid="switch-notifications-all"
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Order Updates</div>
                    <div className="text-sm text-muted-foreground">
                      Get notified about your order status
                    </div>
                  </div>
                  <Switch
                    checked={settings.notifications.orderUpdates}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, orderUpdates: checked },
                      })
                    }
                    data-testid="switch-order-updates"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Social Activity</div>
                    <div className="text-sm text-muted-foreground">
                      Likes, follows, and comments
                    </div>
                  </div>
                  <Switch
                    checked={settings.notifications.socialActivity}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, socialActivity: checked },
                      })
                    }
                    data-testid="switch-social-activity"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Promotional</div>
                    <div className="text-sm text-muted-foreground">
                      Special offers and updates
                    </div>
                  </div>
                  <Switch
                    checked={settings.notifications.promotional}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, promotional: checked },
                      })
                    }
                    data-testid="switch-promotional"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg">Privacy & Sharing</h3>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Show Activity</div>
                  <div className="text-sm text-muted-foreground">
                    Let friends see your reviews and activity
                  </div>
                </div>
                <Switch
                  checked={settings.privacy.showActivity}
                  onCheckedChange={(checked) =>
                    setSettings({
                      ...settings,
                      privacy: { ...settings.privacy, showActivity: checked },
                    })
                  }
                  data-testid="switch-show-activity"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact & Legal */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold">Contact us</h3>
              <a
                href="mailto:support@eatzykudoz.com"
                className="text-sm text-primary hover:underline block"
              >
                support@eatzykudoz.com
              </a>
              <p className="text-xs text-muted-foreground">
                Email us if you need help, have feedback, issue report, or want to connect
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-3">
              <h3 className="font-semibold">Legal</h3>
              <button
                className="text-sm text-foreground hover:text-primary text-left block"
                data-testid="link-terms"
              >
                Terms of Service
              </button>
              <button
                className="text-sm text-foreground hover:text-primary text-left block"
                data-testid="link-privacy"
              >
                Privacy Policy
              </button>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button
            className="w-full"
            onClick={handleSave}
            data-testid="button-save"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
